import { useState, useRef, useEffect } from "react";
import { cn } from "@/lib/cn";
import { Icons } from "@/components/ui/icons";
import { PRESET_COLORS } from "@/workshop/data/mock-project-management";

/**
 * Workshop: ColorPicker — swatch-grid color selector.
 *
 * Renders a trigger button showing the current color swatch. Clicking opens
 * a dropdown with a 4×2 grid of preset colors. The active color shows a
 * white checkmark overlay. Click outside closes the dropdown.
 */

interface ColorPickerProps {
  value: string;
  onChange: (color: string) => void;
  colors?: string[];
  "data-testid"?: string;
}

export function ColorPicker({
  value,
  onChange,
  colors = [...PRESET_COLORS],
  "data-testid": testId = "color-picker",
}: ColorPickerProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;

    function handlePointerDown(e: PointerEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }

    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setOpen(false);
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open]);

  function handleSelect(color: string) {
    onChange(color);
    setOpen(false);
  }

  return (
    <div ref={containerRef} className="relative inline-block" data-testid={testId}>
      {/* Trigger */}
      <button
        type="button"
        className={cn(
          "flex h-6 w-6 items-center justify-center rounded-md border border-glass-stroke transition-opacity hover:opacity-80",
        )}
        style={{ backgroundColor: value }}
        onClick={() => setOpen((prev) => !prev)}
        data-testid={`${testId}-trigger`}
        aria-label="Pick color"
        aria-expanded={open}
      />

      {/* Dropdown */}
      {open && (
        <div
          className={cn(
            "absolute left-0 top-full z-50 mt-1",
            "rounded-lg border border-glass-stroke bg-glass-fill-dense shadow-glass backdrop-blur-md",
            "p-2",
          )}
          data-testid={`${testId}-dropdown`}
        >
          <div className="grid w-[114px] grid-cols-4 gap-1.5">
            {colors.map((color) => {
              const isActive = color === value;
              return (
                <button
                  key={color}
                  type="button"
                  className={cn(
                    "relative flex h-6 w-6 items-center justify-center rounded-md",
                    "transition-opacity hover:opacity-80",
                    isActive && "ring-2 ring-white ring-offset-1 ring-offset-transparent",
                  )}
                  style={{ backgroundColor: color }}
                  onClick={() => handleSelect(color)}
                  data-testid={`color-swatch-${color.replace("#", "")}`}
                  aria-label={`Color ${color}`}
                  aria-pressed={isActive}
                >
                  {isActive && (
                    <Icons.Check className="h-3 w-3 text-white drop-shadow-sm" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export type { ColorPickerProps };
